import { Box, Card, styled, Typography } from "@mui/material";
import { CardContent } from "~/Search/components/ProductResults/views/ListView/components/SearchResultItemCommonElements";
import {
  IMG_CONTRACTS_CALENDAR,
  IMG_CONTRACTS_CART,
  IMG_CONTRACTS_SPEED,
  IMG_CONTRACTS_USERS,
} from "~/utils/url/staticResources";
import { Button } from "~/common/components/Button/Button";
import { CUSTOMER_REGISTER_PAGE } from "~/utils/url/staticUrls";
import { useTranslation } from "react-i18next";

interface Props {
  className?: string;
}

/*
 * Also known as Self Registration Banner.
 * Part of stories
 * https://agile.deloitte.de/browse/SCHAEFF-32822
 * https://agile.deloitte.de/browse/SCHAEFF-32956
 */
export const CustomerRegistrationBanner = ({ className }: Props) => {
  const { t } = useTranslation();
  return (
    <Card elevation={2} className={className}>
      <StyledCardContent>
        <Typography variant={"titleLarge"}>{t("feature.selfRegister.banner.title")}</Typography>
        <BenefitList>
          <Benefit
            iconUrl={IMG_CONTRACTS_CART}
            title={t("feature.selfRegister.banner.benefit.1.title")}
            text={t("feature.selfRegister.banner.benefit.1.text")}
          />
          <Benefit
            iconUrl={IMG_CONTRACTS_CALENDAR}
            title={t("feature.selfRegister.banner.benefit.2.title")}
            text={t("feature.selfRegister.banner.benefit.2.text")}
          />
          <Benefit
            iconUrl={IMG_CONTRACTS_SPEED}
            title={t("feature.selfRegister.banner.benefit.3.title")}
            text={t("feature.selfRegister.banner.benefit.3.text")}
          />
          <Benefit
            iconUrl={IMG_CONTRACTS_USERS}
            title={t("feature.selfRegister.banner.benefit.4.title")}
            text={t("feature.selfRegister.banner.benefit.4.text")}
          />
        </BenefitList>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button sx={{ width: { phone: "100%", tablet: "auto" } }} href={CUSTOMER_REGISTER_PAGE}>
            {t("feature.selfRegister.banner.button")}
          </Button>
        </Box>
      </StyledCardContent>
    </Card>
  );
};

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  padding: "16px",
  [theme.breakpoints.up("tablet")]: {
    padding: "24px",
  },
  [theme.breakpoints.up("desktop-s")]: {
    paddingBlock: "16px",
  },
}));

const BenefitList = styled("div", {
  shouldForwardProp: (prop) => prop !== "theme",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  [theme.breakpoints.up("tablet")]: {
    flexDirection: "row",
    flexWrap: "wrap",
    ["& > *"]: {
      flex: "1 0 45%",
    },
  },
  [theme.breakpoints.up("desktop-s")]: {
    flexWrap: "nowrap",
    ["& > *"]: {
      flex: "1",
    },
  },
}));
interface BenefitProps {
  iconUrl: string;
  title: string;
  text: string;
  className?: string;
}

const Benefit = styled(({ iconUrl, text, title, className }: BenefitProps) => {
  return (
    <div className={className}>
      <img src={iconUrl} alt={""} />
      <div>
        <p>
          <strong>{title}</strong>
        </p>
        <p>{text}</p>
      </div>
    </div>
  );
})({
  display: "flex",
  gap: "16px",
  alignItems: "center",
  img: {
    width: "64px",
    height: "64px",
  },
  p: {
    margin: 0,
  },
});
